import { Children, memo } from 'react';
import { DialogProps } from './Dialog.types';
import { BaseDialog } from '../BaseDialog/BaseDialog';
import { BaseDialogContent } from '../BaseDialogContent/BaseDialogContent';
import { BaseDialogTitle } from '../BaseDialogTitle/BaseDialogTitle';
import { BaseDialogActions } from '../BaseDialogActions/BaseDialogActions';
import { useLocalization } from '../../../context/Localization/localization';

/**
 * A modal component that displays over a backdrop.
 *
 * @param ariaLabel - {@link DialogProps.ariaLabel ariaLabel} description.
 * @param ariaDescribedBy - {@link DialogProps.ariaDescribedBy ariaDescribedBy} description.
 * @param ariaLabelledBy - {@link DialogProps.ariaLabelledBy ariaLabelledBy} description.
 * @param title - {@link DialogProps.title title} description.
 * @param actions - {@link DialogProps.actions actions} description.
 * @param open - {@link DialogProps.open open} description.
 * @param onClose - {@link DialogProps.onClose onClose} description.
 * @param paperDialogClasses - {@link DialogProps.paperDialogClasses paperDialogClasses} description.
 * @param className - {@link DialogProps.className className} description.
 * @param rootDialogContentClasses - {@link DialogProps.rootDialogContentClasses rootDialogContentClasses} description.
 * @param children - {@link DialogProps.children children} description.
 */
const Dialog = memo(({
    ariaLabel,
    ariaDescribedBy,
    title,
    actions,
    open,
    onClose,
    paperDialogClasses,
    className,
    rootDialogContentClasses,
    hideBackdrop,
    children,
}: DialogProps) => {
    const Localization = useLocalization();
    return (
        <BaseDialog
            classes={{
                paper: paperDialogClasses,
            }}
            ariaLabel={ariaLabel}
            ariaDescribedBy={ariaDescribedBy}
            ariaLabelledBy={`dialog-title-${ariaLabel}`}
            open={open}
            onClose={onClose}
            hideBackdrop={hideBackdrop}
        >
            {title ? (
                <BaseDialogTitle
                    id={`dialog-title-${ariaLabel}`}
                    ariaLabel={'Title'}
                    className={className}
                >
                    {title}
                </BaseDialogTitle>
            ) : null}
            {children ? (
                <BaseDialogContent
                    ariaLabel={Localization.Text}
                    classes={{
                        root: rootDialogContentClasses,
                    }}
                >
                    {children}
                </BaseDialogContent>
            ) : null}
            {actions ? (
                <BaseDialogActions
                    ariaLabel={Localization.Actions}
                >
                    {Children.toArray(actions)}
                </BaseDialogActions>
            ) : null}
        </BaseDialog>
    );
});

Dialog.displayName = 'Dialog';

export default Dialog;